// src/store/keepaliveStore.ts
import { create } from "zustand";
import { acceptKeepalive, getCastQueueState, isNoQueue, isStalePrompt } from "../api/cast";
import type { RBEvent } from "../hooks/useRabbitMQ";
import { parseAccepted, parsePrompt, promptApplies, type KeepaliveAccepted, type KeepalivePrompt } from "../lib/castKeepalive";
import { serverClock } from "../lib/castSync/serverClock";
import logger from "../lib/logger";
import { slotEntries } from "../lib/streamSlot";
import { useCastQueueStore } from "./castQueueStore";
import { useOwnCastStore } from "./ownCastStore";
import { findSlot, useStreamStatusStore } from "./streamStatusStore";

/**
 * The keep-alive prompt each slot shows this user (spec 2026-10-19 §4.4; cases C70–C78). Fed by the
 * CAST_KEEPALIVE_* events, GET queue (late join, reconnect, back to the tab), the Accept answers and
 * STOP_STREAM. Nothing here is stored on the server except the queue row's timer.
 */
export interface KeepaliveContext { me: string | null; now(): number; watching(slotId: number): boolean }
export type AcceptResult = "closed" | "retry" | "busy";

export interface KeepaliveState {
  /** Per slot, the prompt this user sees now (only prompts that apply here). */
  bySlot: Record<number, KeepalivePrompt>;
  sending: Record<number, true>;
  onPrompt(p: KeepalivePrompt, ctx: KeepaliveContext): void;
  onAccepted(a: KeepaliveAccepted): void;
  drop(slotId: number): void;
  /** The countdown reached 0 (C76): closes that prompt only, and only in phase ALL (a CASTER deadline hands over to ALL). */
  expire(slotId: number, promptId: number): void;
  /** Closes that prompt if it is over at `now` (isOver: ALL at its deadline, CASTER after the ALL window too). True when it was closed. */
  expireIfOver(slotId: number, promptId: number, now: number): boolean;
  fromEvent(event: RBEvent, ctx: KeepaliveContext): void;
  accept(slotId: number): Promise<AcceptResult>;
  /** Late join, reconnect, return to the tab: GET queue's keepalive. */
  refresh(slotId: number, ctx: KeepaliveContext): void;
  reset(): void;
}

const without = <T,>(m: Record<number, T>, key: number): Record<number, T> => {
  if (!(key in m)) return m;
  const next = { ...m };
  delete next[key];
  return next;
};

/** Phase ALL's length: room-stc sends ALL exactly at the CASTER deadline (prompt_at + 60 s) and ends it at prompt_at + 120 s. */
export const ALL_WINDOW_MS = 60_000;

/**
 * An ALL prompt ends at its deadline. A CASTER prompt past its deadline is still the caster's (spec §4.4:
 * "the caster keeps theirs"; C79) until the ALL window after it is over too, so a very late or lost
 * event can't keep it forever.
 */
export const isOver = (p: KeepalivePrompt, now: number): boolean => now >= p.deadline + (p.phase === "CASTER" ? ALL_WINDOW_MS : 0);

const inflight = new Set<number>();
/** Bumped by reset (sign-out): an answer to a call from before it changes nothing. */
let epoch = 0;

export const useKeepaliveStore = create<KeepaliveState>((set, get) => {
  const dropIf = (slotId: number, promptId: number) =>
    set((s) => (s.bySlot[slotId]?.promptId === promptId ? { bySlot: without(s.bySlot, slotId) } : s));

  return {
    bySlot: {},
    sending: {},

    onPrompt(p, ctx) {
      const held = get().bySlot[p.slotId];
      if (held) {
        // C78: an older prompt, or the CASTER phase of a prompt already in ALL, is stale.
        if (p.promptId < held.promptId) return;
        if (p.promptId === held.promptId && held.phase === "ALL" && p.phase === "CASTER") return;
      }
      if (!promptApplies(p, ctx.me, ctx.watching(p.slotId)) || isOver(p, ctx.now())) {
        if (held) get().drop(p.slotId); // superseded by a prompt that isn't ours, or already over
        return;
      }
      set((s) => ({ bySlot: { ...s.bySlot, [p.slotId]: p } }));
    },

    onAccepted(a) {
      const held = get().bySlot[a.slotId];
      if (held && a.promptId >= held.promptId) get().drop(a.slotId); // C73
    },

    drop: (slotId) => set((s) => (slotId in s.bySlot ? { bySlot: without(s.bySlot, slotId) } : s)),

    expire: (slotId, promptId) => {
      if (get().bySlot[slotId]?.phase === "ALL") dropIf(slotId, promptId);
    },

    expireIfOver(slotId, promptId, now) {
      const held = get().bySlot[slotId];
      if (held?.promptId !== promptId || !isOver(held, now)) return false;
      dropIf(slotId, promptId);
      return true;
    },

    fromEvent(event, ctx) {
      if (event.action !== "STC_EVENT" && event.action !== "GC_EVENT") return;
      const behaviors = new Set((event.metadata.activities ?? []).map((a) => a.behavior));
      if (behaviors.has("CAST_KEEPALIVE_PROMPT")) {
        const p = parsePrompt(event.metadata.keepalive);
        if (p) get().onPrompt(p, ctx);
      }
      if (behaviors.has("CAST_KEEPALIVE_ACCEPTED")) {
        const a = parseAccepted(event.metadata.keepalive);
        if (a) get().onAccepted(a);
      }
      const slot = event.metadata.slot;
      if (slot?.id != null && (slot.available === true || behaviors.has("STOP_STREAM"))) get().drop(slot.id); // C75
    },

    async accept(slotId) {
      const p = get().bySlot[slotId];
      if (!p) return "closed";
      if (get().sending[slotId]) return "busy"; // one Accept call per slot at a time
      set((s) => ({ sending: { ...s.sending, [slotId]: true } }));
      const at = epoch;
      try {
        await acceptKeepalive(slotId, p.promptId);
        if (at === epoch) dropIf(slotId, p.promptId);
        return "closed";
      } catch (e) {
        // C74 (spec §4.4): 409 = already accepted (by someone else, or by us twice); 404 = the cast already stopped.
        if (isStalePrompt(e) || isNoQueue(e)) {
          if (at === epoch) dropIf(slotId, p.promptId);
          return "closed";
        }
        logger.warn("[Keepalive] accept failed for slot", slotId, e);
        return "retry";
      } finally {
        // After a reset the flag is gone already; a later Accept's flag must not be cleared by this old call.
        if (at === epoch) set((s) => ({ sending: without(s.sending, slotId) }));
      }
    },

    refresh(slotId, ctx) {
      if (inflight.has(slotId)) return;
      inflight.add(slotId);
      const at = epoch;
      // C81: a "no prompt" answer closes only what was held when the request went out (a newer PROMPT may arrive meanwhile).
      const sentFor = get().bySlot[slotId]?.promptId;
      const dropStale = () => {
        const held = get().bySlot[slotId];
        if (held && sentFor != null && held.promptId <= sentFor) dropIf(slotId, held.promptId);
      };
      getCastQueueState(slotId)
        .then(
          ({ queue, keepalive }) => {
            if (at !== epoch) return;
            const slot = findSlot(useStreamStatusStore.getState().infoByGc, slotId)?.slot;
            // My own cast in a slot this tab's slot cache doesn't hold (another GC): I am its caster.
            const casterId = slot?.streamingUser?.userId ?? (useOwnCastStore.getState().castBySlotId(slotId) != null ? ctx.me : null);
            const p = parsePrompt(keepalive, slotId, casterId);
            if (p) get().onPrompt(p, ctx);
            else if (keepalive == null) dropStale(); // C72b: accepted while this tab was away
            // The same answer serves castQueueStore's late join (its `ensure`), so the queue isn't fetched twice.
            const queues = useCastQueueStore.getState();
            if (queue && !queues.bySlot[slotId] && slot?.available !== true) {
              queues.take(slotId, queue, typeof slot?.timelineRev === "number" ? slot.timelineRev : null);
            }
          },
          (e: unknown) => {
            if (at !== epoch) return;
            if (isNoQueue(e)) dropStale(); // C72b: the cast is gone
            else logger.warn("[Keepalive] queue fetch failed for slot", slotId, e);
          },
        )
        .finally(() => {
          // After a reset a newer refresh may be in flight for this slot: only the current epoch's call clears it.
          if (at === epoch) inflight.delete(slotId);
        });
    },

    reset() {
      epoch++;
      inflight.clear();
      set({ bySlot: {}, sending: {} });
    },
  };
});

/** Real context: the server clock; watching = the slot is busy in this tab's slot cache, or it is this tab's own cast. */
export function keepaliveContextOf(me: string | null): KeepaliveContext {
  return {
    me,
    now: () => serverClock.now(),
    watching: (slotId) =>
      findSlot(useStreamStatusStore.getState().infoByGc, slotId)?.slot.available === false ||
      useOwnCastStore.getState().castBySlotId(slotId) != null,
  };
}

/**
 * The prompt to show at server time `now`: the live one (deadline ahead) that runs out first; only
 * when none is live, a CASTER prompt held at 0 s (waiting for its ALL phase). Prompts that are over
 * (isOver) are skipped, so a stuck one never hides another slot's live dialog. Returns a stored reference.
 */
export function firstPrompt(bySlot: Record<number, KeepalivePrompt>, now: number): KeepalivePrompt | null {
  let first: KeepalivePrompt | null = null;
  const live = (p: KeepalivePrompt) => now < p.deadline;
  for (const p of Object.values(bySlot)) {
    if (isOver(p, now)) continue;
    if (!first || (live(p) && !live(first)) || (live(p) === live(first) && p.deadline < first.deadline)) first = p;
  }
  return first;
}

/** C75: a slot the list shows free has no prompt (a STOP_STREAM missed during a reconnect). */
useStreamStatusStore.subscribe((status, prev) => {
  if (status.infoByGc === prev.infoByGc) return;
  const { bySlot, drop } = useKeepaliveStore.getState();
  for (const info of Object.values(status.infoByGc)) {
    for (const { slot } of slotEntries(info)) if (slot.available === true && slot.id in bySlot) drop(slot.id);
  }
});
